import type {
  ComponentVisibility,
  FormComponent,
  FormDSLContext,
  FormStep,
  ValidationRules,
} from '@commenda/types';
import { isComponentReadOnly, isValueEmpty } from '../utils/form-state';
import {
  getTestValueForComponent,
  SELF_FILL_COMPONENT_TYPES,
} from './test-value-registry';

export interface PrefillAssignment {
  fieldId: string;
  value: unknown;
  /**
   * The component's validation rules, so the applier can run validateField
   * right after updateField — exactly what a field wrapper does on typing.
   * Without this, errors recorded by an earlier empty-step validation linger
   * and block Next even though the values are now present.
   */
  rules?: ValidationRules;
  /**
   * For repeatable-module assignments (value = the whole instance array):
   * the per-instance field validations to run after the write, so errors
   * keyed `saveKey.index.fieldId` from an earlier empty validation clear —
   * the array-level validateField call can't reach them.
   */
  instanceFieldValidations?: {
    fieldName: string;
    value: unknown;
    rules?: ValidationRules;
  }[];
}

export interface PrefillSkip {
  fieldId: string;
  label: string;
  reason: string;
}

export interface StepPrefillPlan {
  assignments: PrefillAssignment[];
  skipped: PrefillSkip[];
  /** Visible, enabled, fillable fields that already had a value (left untouched). */
  alreadyFilledCount: number;
  /**
   * Fields that received a value. Differs from assignments.length when a
   * repeatable module is filled: its instances are written back as ONE
   * assignment (the whole array under the module's saveKey) that may carry
   * many field fills.
   */
  filledFieldCount: number;
  /**
   * Fields whose component registered a preview fill handler (it fills itself
   * from its own loaded data). The executor invokes these and merges their
   * outcomes into its own final counts.
   */
  handlerCandidates: { fieldId: string; label: string }[];
}

interface ComputeStepPrefillArgs {
  step: FormStep | undefined;
  formData: Record<string, unknown>;
  dslContext?: FormDSLContext;
  isFieldVisible: (visibility?: ComponentVisibility) => boolean;
  isFieldDisabled: (fieldName?: string) => boolean;
  isPreview: boolean;
  /** Whether a component registered a self-fill handler for this fieldId. */
  hasFillHandler?: (fieldId: string) => boolean;
}

const componentLabel = (component: FormComponent): string =>
  component.label ?? component.fieldId ?? component.component;

const REPEATABLE_MODULE_EMPTY_SKIP_REASON =
  'no entries yet — add one first';

interface RepeatableModuleFillResult {
  updatedInstances: Record<string, unknown>[];
  filledFieldCount: number;
  alreadyFilledCount: number;
  skipped: PrefillSkip[];
  instanceFieldValidations: {
    fieldName: string;
    value: unknown;
    rules?: ValidationRules;
  }[];
}

// Fill empty fields inside a repeatable module's EXISTING instances. Never
// creates or removes instances — how many entries exist is the tester's
// choice; pre-fill only populates what's on screen. Instance-scoped visibility
// conditions are approximated with the top-level check; worst case a
// per-instance-hidden field gets a value, which validation ignores anyway.
function fillRepeatableInstances(
  module: { components?: FormComponent[] },
  instances: Record<string, unknown>[],
  saveKey: string,
  isFieldVisible: (visibility?: ComponentVisibility) => boolean,
  dslContext?: FormDSLContext
): RepeatableModuleFillResult {
  let filledFieldCount = 0;
  let alreadyFilledCount = 0;
  const skipped: PrefillSkip[] = [];
  const instanceFieldValidations: {
    fieldName: string;
    value: unknown;
    rules?: ValidationRules;
  }[] = [];

  const updatedInstances = instances.map((instance, instanceIndex) => {
    const updatedInstance: Record<string, unknown> = { ...(instance ?? {}) };
    (module.components ?? []).forEach((component) => {
      const { fieldId } = component;
      if (!fieldId) return;
      if (component.component === 'FormSummary') return;
      if (!isFieldVisible(component.visibility)) return;

      if (!isValueEmpty(updatedInstance[fieldId])) {
        alreadyFilledCount += 1;
        return;
      }

      const result = getTestValueForComponent(component, dslContext, instanceIndex);
      if (result.kind === 'value') {
        updatedInstance[fieldId] = result.value;
        filledFieldCount += 1;
        instanceFieldValidations.push({
          fieldName: `${saveKey}.${instanceIndex}.${fieldId}`,
          value: result.value,
          rules: component.validation?.rules,
        });
      } else if (result.kind === 'skip') {
        skipped.push({
          fieldId: `${saveKey}.${instanceIndex}.${fieldId}`,
          label: componentLabel(component),
          reason: result.reason,
        });
      }
    });
    return updatedInstance;
  });

  return {
    updatedInstances,
    filledFieldCount,
    alreadyFilledCount,
    skipped,
    instanceFieldValidations,
  };
}

/**
 * Decides, for one step, which empty fields to fill and with what — mirroring
 * the renderer's non-repeatable field traversal in validateCurrentStep. Pure:
 * it computes a plan; the caller applies assignments through updateField so the
 * values are indistinguishable from typed input.
 *
 * Non-fillable fields fall into three buckets: already-filled (left untouched,
 * counted), reported skips (dynamic options, entity fields, disabled/missing
 * context, repeatable/sub-entry), and silent skips (display-only, read-only OS
 * fields that preview already exempts from required validation).
 *
 * Repeatable modules are out of scope for v1 fill and reported as a single skip.
 */
export function computeStepPrefill({
  step,
  formData,
  dslContext,
  isFieldVisible,
  isFieldDisabled,
  isPreview,
  hasFillHandler,
}: ComputeStepPrefillArgs): StepPrefillPlan {
  const assignments: PrefillAssignment[] = [];
  const skipped: PrefillSkip[] = [];
  const handlerCandidates: { fieldId: string; label: string }[] = [];
  let alreadyFilledCount = 0;
  let repeatableFilledFieldCount = 0;
  let repeatableAssignmentCount = 0;

  if (!step) {
    return {
      assignments,
      skipped,
      alreadyFilledCount: 0,
      filledFieldCount: 0,
      handlerCandidates,
    };
  }

  step.modules?.forEach((module) => {
    if (
      module.visibility &&
      !isFieldVisible(module.visibility as ComponentVisibility)
    ) {
      return;
    }

    if ((module.repeatable as { enabled?: boolean } | undefined)?.enabled) {
      const saveKey = module.saveKey || module.id;
      const rawInstances = formData[saveKey];
      // Safe: Array.isArray-guarded; instance entries are the renderer's own
      // per-instance formData objects.
      const instances = Array.isArray(rawInstances)
        ? (rawInstances as Record<string, unknown>[])
        : [];

      if (instances.length === 0) {
        skipped.push({
          fieldId: saveKey,
          label: module.name || module.id,
          reason: REPEATABLE_MODULE_EMPTY_SKIP_REASON,
        });
        return;
      }

      const instancesFill = fillRepeatableInstances(
        module,
        instances,
        saveKey,
        isFieldVisible,
        dslContext
      );
      alreadyFilledCount += instancesFill.alreadyFilledCount;
      skipped.push(...instancesFill.skipped);
      if (instancesFill.filledFieldCount > 0) {
        // One assignment writes the whole instance array back; per-instance
        // validations ride along so `saveKey.index.fieldId` errors clear.
        assignments.push({
          fieldId: saveKey,
          value: instancesFill.updatedInstances,
          instanceFieldValidations: instancesFill.instanceFieldValidations,
        });
        repeatableFilledFieldCount += instancesFill.filledFieldCount;
        repeatableAssignmentCount += 1;
      }
      return;
    }

    module.components?.forEach((component) => {
      const { fieldId } = component;
      if (!isFieldVisible(component.visibility)) return;
      if (!fieldId) return;
      if (component.component === 'FormSummary') return;

      // Self-filling components own their emptiness/skip logic — their value
      // shapes (e.g. KeyPersons' entries map) defeat the generic empty check.
      // Routed by type as well as registration: collapsed modules unmount
      // their components, so a handler may register only after expansion.
      if (
        hasFillHandler?.(fieldId) ||
        SELF_FILL_COMPONENT_TYPES.has(component.component)
      ) {
        handlerCandidates.push({ fieldId, label: componentLabel(component) });
        return;
      }

      if (!isValueEmpty(formData[fieldId])) {
        alreadyFilledCount += 1;
        return;
      }

      if (isFieldDisabled(fieldId)) {
        skipped.push({
          fieldId,
          label: componentLabel(component),
          reason: 'disabled — waiting for context',
        });
        return;
      }

      if (isPreview && isComponentReadOnly(component)) {
        // Read-only OS fields are exempt from required validation in preview;
        // leave them alone silently.
        return;
      }

      const result = getTestValueForComponent(component, dslContext);
      if (result.kind === 'value') {
        assignments.push({
          fieldId,
          value: result.value,
          rules: component.validation?.rules,
        });
      } else if (result.kind === 'skip') {
        skipped.push({
          fieldId,
          label: componentLabel(component),
          reason: result.reason,
        });
      }
    });
  });

  // Non-repeatable fills contribute one field per assignment; repeatable
  // fills are bundled into single array assignments, counted separately.
  const filledFieldCount =
    assignments.length - repeatableAssignmentCount + repeatableFilledFieldCount;

  return {
    assignments,
    skipped,
    alreadyFilledCount,
    filledFieldCount,
    handlerCandidates,
  };
}
